<script lang="ts" setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/solid'
import {
  FolderIcon,
  GlobeAltIcon,
  ArrowPathIcon,
} from '@heroicons/vue/24/outline'
import { PlusIcon } from '@heroicons/vue/20/solid'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { useAppLayout } from '@/stores/appLayout'
import { storeToRefs } from 'pinia'
import { usePage } from '@inertiajs/vue3'
import { CommandPaletteAction } from '@/Support/CommandPalette/Contracts/CommandPalette'
import LoadingIcon from '@/Components/App/Global/LoadingIcon.vue'
import { ItemSearchable } from '@/Support/Common/Interfaces/ItemSearchable'
import { routing } from '@/Support/Routing/Functions/routing'
import { SearchableData } from '@/Support/Common/Interfaces/SearchableData'
import { t } from '@/Support/lang'
import { bgModal } from '@/Support/Styles/Global/ModalStyles'
import _ from 'lodash'
import { inertiaModalVisit } from '@/Support/Routing/Functions/inertiaModalVisit'
import AppIcon from '@/Components/App/Global/AppIcon.vue'
import collect from 'collect.js'
import { getAppIcon } from '@/Support/Common/Interfaces/appIcons'
import { useChangeLanguage } from '@/Support/Languages/Composables/useChangeLanguage'
import { changeDarkMode } from '@/Support/Common/Functions/changeDarkMode'
import { useTeams } from '@/Support/Teams/Composables/useTeams'
import { useCommandPaletteSearch } from '@/Support/CommandPalette/Composables/useCommandPaletteSearch'

const { allTeamAsCommands, switchToTeam } = useTeams()

const store = useAppLayout()

const allTeams = ref(false)

const actions = computed(() =>
  (
    usePage().props?.commandPaletteActions as unknown as CommandPaletteAction[]
  ).concat(allTeamAsCommands.value),
)

interface SearchGlobalLabels {
  searchAllTeams: string
  searchTeam: string
}

const comboboxInput =
  useTemplateRef<InstanceType<typeof ComboboxInput>>('input')

const searchGlobalLabels = computed(
  () =>
    usePage().props
      ?.commandPaletteGlobalSearch as unknown as SearchGlobalLabels,
)

const { showCommandPalette } = storeToRefs(store)
const query = ref('')

const { filteredActions } = useCommandPaletteSearch(actions, query)

const getActionName = (action: CommandPaletteAction) => {
  switch (action.value) {
    case 'all_teams_search':
      return getPlaceholder(!allTeams.value)

    default:
      if (action.highlights?.name) {
        return highlight(action.name, action.highlights.name)
      }
      return action.name
  }
}

/**
 * SSE
 */
let searchSource: EventSource | null = null
const hasSearched = ref(false)

const resetSearchState = () => {
  searchablesData.value = {}
  loading.value = {}

  itemsSearchables.value.forEach(item => {
    loading.value[item.value] = true
  })
}

const { openCommandPalette, openAiQuery } = store
const { updateAndChange } = useChangeLanguage()

function onSelect(item: CommandPaletteAction) {
  switch (item.type) {
    case 'searchAllTeams':
      toggleAllTeamsSearch()
      break

    case 'aiPrompt':
      showCommandPalette.value = false
      openAiQuery()
      break

    case 'loadRecent':
      loadRecentItems()
      break

    case 'changeLanguage':
      const language = item.url.split('.')[1]
      updateAndChange(language)
      close()
      break

    case 'changeTheme':
      const theme = item.url.split('.')[1]
      changeDarkMode(theme)
      close()
      break

    case 'switchTeam':
      switchToTeam(item.value)
      close()
      break

    default:
      openCommandPalette(false)
      inertiaModalVisit(item.url, { preserveState: false })
      break
  }
}

const querying = _.debounce((event: InputEvent) => {
  query.value = (event.target as HTMLInputElement).value
  loadRecentItems(query.value)
}, 400)

const itemsList =
  useTemplateRef<InstanceType<typeof ComboboxOptions>>('itemsList')
const scrollTop = () => {
  nextTick(() => {
    if (itemsList.value) {
      itemsList.value.$el.scrollTop = 0
    }
  })
}

/**
 * Searching
 */
const globalLoading = ref(false)
const loading = ref<{
  [index: string]: boolean
}>({})
const itemsSearchables = computed(
  () => usePage().props.itemsSearchables as ItemSearchable[],
)
const searchablesData = ref<{
  [index: string]: SearchableData[]
}>({})
const afterLoading = () => {
  searchSource?.close()
  searchSource = null
  globalLoading.value = false
  scrollTop()
}

/**
 * Loaders
 */

const loadBySSE = ref(false)
const loadRecentItemsBySSE = (text: string | null = null) => {
  scrollTop()
  hasSearched.value = true
  globalLoading.value = true

  if (searchSource) {
    searchSource.close()
    searchSource = null
  }

  resetSearchState()

  searchSource = new EventSource(
    routing('global.search.sse.index', {
      text,
      all_teams: allTeams.value,
    }),
  )

  searchSource.addEventListener('result', (event: MessageEvent) => {
    const payload = JSON.parse(event.data)

    searchablesData.value[payload.model] = payload.items
    loading.value[payload.model] = false
  })

  searchSource.addEventListener('done', () => {
    Object.keys(loading.value).forEach(key => {
      loading.value[key] = false
    })

    afterLoading()
  })

  searchSource.onerror = () => {
    afterLoading()
  }
}

const loadRecentItemsClassic = (text: string | null = null) => {
  scrollTop()
  hasSearched.value = true
  globalLoading.value = true

  resetSearchState()

  const requests = itemsSearchables.value.map(item => {
    return window.axios
      .get(
        routing('global.search.index', {
          model: item.value,
          text,
          all_teams: allTeams.value,
        }),
      )
      .then(response => {
        searchablesData.value[item.value] = response.data as SearchableData[]
        loading.value[item.value] = false
      })
  })

  Promise.allSettled(requests).finally(() => {
    globalLoading.value = false
    scrollTop()
  })
}
const loadRecentItems = (text: string | null = null) => {
  if (loadBySSE.value) {
    loadRecentItemsBySSE(text)
  } else {
    loadRecentItemsClassic(text)
  }
}

const placeholder = computed(() => getPlaceholder(allTeams.value))

const getPlaceholder = (allTeams: boolean) =>
  allTeams
    ? searchGlobalLabels.value.searchAllTeams
    : searchGlobalLabels.value.searchTeam

const toggleAllTeamsSearch = () => {
  allTeams.value = !allTeams.value
  comboboxInput.value?.$el.focus()
  loadRecentItems(query.value)
}

defineExpose({
  loadRecentItems,
})

const close = () => {
  showCommandPalette.value = false
}

// Highlights actions
function highlight(text: string, indexs: number[][] = []): string {
  if (!indexs.length) return text

  const filtered = indexs.filter(([start, end]) => end - start + 1 >= 2)

  let highlighted = ''
  let lastIndex = 0

  filtered.forEach(([start, end]) => {
    highlighted += text.slice(lastIndex, start)
    highlighted += `<span class="font-bold text-accent-600 dark:text-accent-400">${text.slice(start, end + 1)}</span>`
    lastIndex = end + 1
  })

  highlighted += text.slice(lastIndex)
  return highlighted
}

watch(showCommandPalette, (open: boolean) => {
  if (!open) {
    if (searchSource) {
      searchSource.close()
      searchSource = null
    }
    allTeams.value = false
  }
})

const onLeave = () => {
  query.value = ''
  searchablesData.value = {}
}
</script>

<template>
  <TransitionRoot
    :show="showCommandPalette"
    appear
    as="template"
    @after-leave="onLeave"
  >
    <Dialog as="div" class="relative z-10" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div :class="bgModal" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="mx-auto max-w-2xl transform divide-y divide-neutral-500/10 dark:divide-neutral-500/20 overflow-hidden rounded-xl bg-white/80 dark:bg-neutral-900/80 shadow-2xl ring-1 ring-black/5 backdrop-blur-sm backdrop-filter transition-all"
          >
            <Combobox @update:modelValue="onSelect">
              <div class="relative">
                <ArrowPathIcon
                  class="animate-spin absolute top-3.5 left-4 h-5 w-5 text-neutral-900/40 dark:text-neutral-400/40"
                  v-if="globalLoading"
                  :loading="globalLoading"
                />
                <GlobeAltIcon
                  v-else-if="allTeams"
                  v-tooltip="searchGlobalLabels.searchTeam"
                  aria-hidden="true"
                  class="cursor-pointer absolute top-3.5 left-4 h-5 w-5 text-neutral-900/40 dark:text-neutral-400/40"
                  @click="toggleAllTeamsSearch"
                />
                <MagnifyingGlassIcon
                  v-else
                  v-tooltip="searchGlobalLabels.searchAllTeams"
                  aria-hidden="true"
                  class="cursor-pointer absolute top-3.5 left-4 h-5 w-5 text-neutral-900/40 dark:text-neutral-400/40"
                  @click="toggleAllTeamsSearch"
                />
                <ComboboxInput
                  ref="input"
                  :placeholder="placeholder"
                  class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-neutral-900 dark:text-white placeholder-neutral-500 focus:ring-0 sm:text-sm"
                  @change="querying"
                />
              </div>

              <ComboboxOptions
                ref="itemsList"
                class="max-h-80 scroll-py-2 divide-y divide-neutral-500/10 dark:divide-neutral-500/20 overflow-y-auto"
                static
              >
                <template v-for="item in itemsSearchables" :key="item.value">
                  <li
                    v-if="
                      hasSearched &&
                      (loading[item.value] ||
                        searchablesData[item.value]?.length)
                    "
                    class="p-2"
                  >
                    <h2
                      class="flex items-center mt-4 mb-2 px-3 text-xs font-semibold text-neutral-900 dark:text-neutral-200"
                    >
                      {{ __(item.description) }}
                      <LoadingIcon
                        :loading="loading[item.value]"
                        class="text-neutral-500 dark:text-neutral-400"
                      />
                    </h2>
                    <ul class="text-sm text-neutral-700 dark:text-neutral-400">
                      <ComboboxOption
                        v-for="(search, model) in searchablesData[item.value]"
                        :key="search.id"
                        v-slot="{ active }"
                        :value="search"
                        as="template"
                      >
                        <li
                          :class="[
                            'flex cursor-default select-none justify-between items-center rounded-md px-3 py-2',
                            active &&
                              'bg-neutral-900/5 text-neutral-900 dark:bg-neutral-800 dark:text-white',
                          ]"
                        >
                          <div class="flex items-start">
                            <component
                              :is="
                                getAppIcon(item.value)?.component || FolderIcon
                              "
                              :class="[
                                'h-6 w-6 flex-none text-neutral-900/40',
                                active
                                  ? 'text-opacity-100 dark:text-white'
                                  : 'dark:text-neutral-500',
                              ]"
                              aria-hidden="true"
                            />
                            <div class="flex flex-col ml-3">
                              <span
                                v-if="typeof search.subject === 'string'"
                                class="ml-3 flex-auto truncate"
                              >
                                {{ search.subject }}
                              </span>

                              <div v-else>
                                <span
                                  v-for="(sub, index) in search.subject"
                                  :key="index"
                                  :class="{
                                    'font-bold text-accent-600 dark:text-accent-400':
                                      index === 1,
                                  }"
                                >
                                  {{ sub }}
                                </span>
                              </div>
                              <div
                                class="text-xxs truncate mt-2 text-primary-700 dark:text-primary-200"
                              >
                                {{ t('global-search.found-in') }}
                                <span class="font-semibold uppercase">
                                  {{ search.title }}
                                </span>
                              </div>
                            </div>
                          </div>
                          <span
                            v-if="active"
                            class="ml-3 flex-none text-neutral-500 dark:text-neutral-400"
                          >
                            {{ __('Jump to ...') }}
                          </span>
                        </li>
                      </ComboboxOption>
                    </ul>
                  </li>
                </template>

                <li class="p-2">
                  <h2 class="sr-only">{{ __('Actions') }}</h2>
                  <ul class="text-sm text-neutral-700 dark:text-neutral-400">
                    <ComboboxOption
                      v-for="action in filteredActions"
                      :key="action.value"
                      v-slot="{ active }"
                      :value="action"
                      as="template"
                    >
                      <li
                        :class="[
                          'flex cursor-default select-none items-center rounded-md px-3 py-2',
                          active &&
                            'bg-neutral-900/5 text-neutral-900 dark:bg-neutral-800 dark:text-white',
                        ]"
                      >
                        <div class="relative">
                          <AppIcon
                            :icon="action.icon"
                            :class="[
                              'h-6 w-6 flex-none text-neutral-900/40',
                              active
                                ? 'text-opacity-100 dark:text-white'
                                : 'dark:text-neutral-500',
                            ]"
                            aria-hidden="true"
                          />
                          <PlusIcon
                            v-if="action.type === 'create'"
                            :class="[
                              'absolute h-4 w-4 top-0 right-0 -m-2 text-primary-500',
                              active
                                ? 'text-opacity-100 dark:text-white'
                                : 'dark:text-neutral-500',
                            ]"
                          />
                        </div>
                        <div
                          class="ml-3 flex-auto truncate"
                          v-html="getActionName(action)"
                        ></div>
                        <span
                          class="ml-3 flex-none text-xs font-semibold text-neutral-500 dark:text-neutral-400"
                        >
                          <!--                          <kbd class="font-sans">⌘</kbd>-->
                          <!--                          <kbd class="font-sans">{{ action.shortcut }}</kbd>-->
                        </span>
                      </li>
                    </ComboboxOption>
                  </ul>
                </li>
              </ComboboxOptions>

              <!--              <div-->
              <!--                v-if="query !== '' && filteredProjects.length === 0"-->
              <!--                class="py-14 px-6 text-center sm:px-14"-->
              <!--              >-->
              <!--                <FolderIcon-->
              <!--                  aria-hidden="true"-->
              <!--                  class="mx-auto h-6 w-6 text-neutral-900/40 dark:text-neutral-500"-->
              <!--                />-->
              <!--                <p class="mt-4 text-sm text-neutral-900">-->
              <!--                  We couldn't find any projects with that term. Please try-->
              <!--                  again.-->
              <!--                </p>-->
              <!--              </div>-->
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
